import React, { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Popover,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconEdit, IconX } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import {
  CashDrawerBalancingSummary,
  CashDrawerSession,
} from "../../types/cashDrawer";
import {
  formatBalancingDate,
  formatCurrency,
} from "../../utils/cashDrawerUtils";

interface CashDrawerBalancingSummaryModalProps {
  opened: boolean;
  onClose: () => void;
  /** UUID of the closed cash drawer session to summarize. */
  sessionId: string | null;
  /** Salon timezone — used only for the DateInput edit popover so the
   *  picker stays consistent with the rest of the cash drawer UI. */
  timezone: string;
}

const BILL_ROWS: Array<{
  key: keyof CashDrawerBalancingSummary["counted_cash"]["ending_denominations"];
  label: string;
}> = [
  { key: "hundred", label: "$ in Hundreds" },
  { key: "fifty", label: "$ in Fifties" },
  { key: "twenty", label: "$ in Twenties" },
  { key: "ten", label: "$ in Tens" },
  { key: "five", label: "$ in Fives" },
  { key: "two", label: "$ in Twos" },
  { key: "one", label: "$ in Ones" },
  { key: "coins", label: "$ in Coins" },
];

// The hardcoded six match the legacy reconciliation rows. Per-salon
// custom Off-Platform Payment methods (Care Credit, Groupon, etc.)
// render below this list via summary.daily_sales.custom_payments, using
// the same row shape so each appears as a first-class line.
const PAYMENT_ROWS: Array<{
  key: "cash" | "check" | "zelle" | "venmo" | "cashapp" | "card";
  label: string;
}> = [
  { key: "cash", label: "Cash Transactions" },
  { key: "check", label: "Check Transactions" },
  { key: "zelle", label: "Zelle Transactions" },
  { key: "venmo", label: "Venmo Transactions" },
  { key: "cashapp", label: "Cash App Transactions" },
  { key: "card", label: "Card Transactions" },
];

export const CashDrawerBalancingSummaryModal: React.FC<
  CashDrawerBalancingSummaryModalProps
> = ({ opened, onClose, sessionId, timezone }) => {
  const { getCashDrawerBalancingSummary, updateCashDrawerBalancingDate } =
    useApp();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<CashDrawerSession | null>(null);
  const [summary, setSummary] = useState<CashDrawerBalancingSummary | null>(
    null,
  );
  const [editPopoverOpen, setEditPopoverOpen] = useState(false);
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [savingDate, setSavingDate] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getCashDrawerBalancingSummary(sessionId);
      setSession(result.session);
      setSummary(result.summary);
    } catch (e: any) {
      setError(e?.message || "Failed to load balancing summary");
    } finally {
      setLoading(false);
    }
  }, [getCashDrawerBalancingSummary, sessionId]);

  useEffect(() => {
    if (opened && sessionId) {
      loadSummary();
    } else if (!opened) {
      setSession(null);
      setSummary(null);
      setError(null);
      setEditPopoverOpen(false);
    }
  }, [opened, sessionId, loadSummary]);

  const handleSaveDate = async () => {
    if (!sessionId || !editingDate) return;
    setSavingDate(true);
    setError(null);
    try {
      await updateCashDrawerBalancingDate(sessionId, editingDate);
      setEditPopoverOpen(false);
      await loadSummary();
    } catch (e: any) {
      setError(e?.message || "Failed to update balancing date");
    } finally {
      setSavingDate(false);
    }
  };

  const overShort = summary?.daily_sales.cash_over_short ?? 0;
  const overShortLabel =
    overShort === 0 ? "Balanced" : overShort > 0 ? "Over by" : "Short by";
  const overShortColor =
    overShort === 0 ? undefined : overShort > 0 ? "green.7" : "red.7";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      styles={{
        body: { overflow: "hidden", padding: 0 },
        content: { overflow: "hidden" },
      }}
    >
      <Box
        h="100dvh"
        mah="100dvh"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <Group
          justify="space-between"
          wrap="nowrap"
          px="lg"
          py="md"
          style={{
            borderBottom: "1px solid var(--mantine-color-gray-2)",
            flex: "0 0 auto",
          }}
        >
          <Group gap={12} wrap="nowrap" style={{ minWidth: 0 }}>
            <Title order={3} fz={22} style={{ whiteSpace: "nowrap" }}>
              Cash Drawer Balancing Report
            </Title>
          </Group>
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={onClose}
              aria-label="Close"
            >
              <IconX size={20} />
            </ActionIcon>
          </Group>
        </Group>

        <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
          <Box pos="relative" mih={200} p="lg">
            <LoadingOverlay visible={loading} zIndex={10} />

            {error && (
              <Alert color="red" variant="light" mb="md">
                {error}
              </Alert>
            )}

            {summary && session && (
              <Stack gap="lg">
                {/* === Counted Cash Summary === */}
                <Paper withBorder p="md">
                  <Title order={4} mb="sm">
                    Counted Cash Summary
                  </Title>
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                    <Paper withBorder p="sm" bg="var(--mantine-color-gray-0)">
                      <Stack gap={4}>
                        <Group justify="space-between" wrap="nowrap">
                          <Text
                            size="xs"
                            tt="uppercase"
                            fw={600}
                            c="dimmed"
                            style={{ letterSpacing: "0.08em" }}
                          >
                            Starting Count
                          </Text>
                          <Text fz={22} fw={700}>
                            {formatCurrency(
                              summary.counted_cash.starting_amount,
                            )}
                          </Text>
                        </Group>
                        <Divider variant="dashed" my={2} />
                        <Group justify="space-between" wrap="nowrap">
                          <Text
                            size="xs"
                            tt="uppercase"
                            fw={600}
                            c="dimmed"
                            style={{ letterSpacing: "0.08em" }}
                          >
                            Ending Count
                          </Text>
                          <Text fz={22} fw={700}>
                            {formatCurrency(summary.counted_cash.ending_amount)}
                          </Text>
                        </Group>
                        <Divider variant="dashed" my={2} />
                        <Group justify="space-between" wrap="nowrap">
                          <Text
                            size="xs"
                            tt="uppercase"
                            fw={600}
                            c="dimmed"
                            style={{ letterSpacing: "0.08em" }}
                          >
                            Counted Checks
                          </Text>
                          <Text fz={22} fw={700}>
                            {summary.counted_cash.counted_checks === null
                              ? "—"
                              : formatCurrency(
                                  summary.counted_cash.counted_checks,
                                )}
                          </Text>
                        </Group>
                      </Stack>
                    </Paper>
                    <Paper withBorder p="sm" bg="var(--mantine-color-gray-0)">
                      <Text
                        size="xs"
                        tt="uppercase"
                        fw={700}
                        c="dimmed"
                        mb={6}
                        style={{ letterSpacing: "0.08em" }}
                      >
                        Ending Denominations
                      </Text>
                      <Stack gap={2}>
                        {BILL_ROWS.map((row) => (
                          <Group
                            key={row.key}
                            justify="space-between"
                            wrap="nowrap"
                          >
                            <Text size="sm">{row.label}</Text>
                            <Text size="sm" fw={600}>
                              {formatCurrency(
                                summary.counted_cash.ending_denominations[
                                  row.key
                                ],
                              )}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    </Paper>
                  </SimpleGrid>
                </Paper>

                {/* === Daily Sales Summary === */}
                <Paper withBorder p="md">
                  <Group justify="space-between" wrap="wrap" gap="sm" mb="sm">
                    <Title order={4}>
                      Daily Sales Summary for{" "}
                      {formatBalancingDate(summary.balancing_date)}
                    </Title>
                    <Popover
                      position="bottom-end"
                      withArrow
                      opened={editPopoverOpen}
                      onChange={setEditPopoverOpen}
                    >
                      <Popover.Target>
                        <Button
                          variant="subtle"
                          color="var(--brand-teal)"
                          size="xs"
                          leftSection={<IconEdit size={14} />}
                          onClick={() => {
                            setEditingDate(summary.balancing_date);
                            setEditPopoverOpen((o) => !o);
                          }}
                        >
                          Edit Date
                        </Button>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <Stack gap="sm">
                          <DateInput
                            label="Balancing Date"
                            value={editingDate}
                            onChange={(val) => setEditingDate(val ?? null)}
                            popoverProps={{ withinPortal: true }}
                          />
                          <Group justify="flex-end" gap="xs">
                            <Button
                              variant="subtle"
                              size="xs"
                              onClick={() => setEditPopoverOpen(false)}
                              disabled={savingDate}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="xs"
                              color="var(--brand-teal)"
                              onClick={handleSaveDate}
                              loading={savingDate}
                              disabled={!editingDate}
                            >
                              Save
                            </Button>
                          </Group>
                          <Text size="xs" c="dimmed">
                            Changing the date refreshes the Daily Sales Summary
                            for the new salon-local day ({timezone}).
                          </Text>
                        </Stack>
                      </Popover.Dropdown>
                    </Popover>
                  </Group>
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                    <Paper withBorder p="sm" bg="var(--mantine-color-gray-0)">
                      <Stack gap={2}>
                        {PAYMENT_ROWS.map((row) => (
                          <Group
                            key={row.key}
                            justify="space-between"
                            wrap="nowrap"
                          >
                            <Text size="sm">{row.label}</Text>
                            <Text size="sm" fw={600}>
                              {formatCurrency(summary.daily_sales[row.key])}
                            </Text>
                          </Group>
                        ))}
                        {(summary.daily_sales.custom_payments ?? []).map(
                          (entry) => (
                            <Group
                              key={`custom-${entry.name}`}
                              justify="space-between"
                              wrap="nowrap"
                            >
                              <Text size="sm">{entry.name} Transactions</Text>
                              <Text size="sm" fw={600}>
                                {formatCurrency(entry.amount)}
                              </Text>
                            </Group>
                          ),
                        )}
                      </Stack>
                    </Paper>
                    <Paper withBorder p="sm" bg="var(--mantine-color-gray-0)">
                      <Stack gap={2}>
                        <Group justify="space-between" wrap="nowrap">
                          <Text size="sm">Total Service Sales</Text>
                          <Text size="sm" fw={600}>
                            {formatCurrency(summary.daily_sales.service_sales)}
                          </Text>
                        </Group>
                        <Group justify="space-between" wrap="nowrap">
                          <Text size="sm">Net Retail Product Sales</Text>
                          <Text size="sm" fw={600}>
                            {formatCurrency(
                              summary.daily_sales.net_retail_sales,
                            )}
                          </Text>
                        </Group>
                        <Group justify="space-between" wrap="nowrap">
                          <Text size="sm">Tips</Text>
                          <Text size="sm" fw={600}>
                            {formatCurrency(summary.daily_sales.tips)}
                          </Text>
                        </Group>
                        <Group justify="space-between" wrap="nowrap">
                          <Text size="sm">Taxes</Text>
                          <Text size="sm" fw={600}>
                            {formatCurrency(summary.daily_sales.taxes)}
                          </Text>
                        </Group>
                        <Divider my={4} />
                        <Group justify="space-between" wrap="nowrap">
                          <Text fw={700}>Total</Text>
                          <Text fw={700}>
                            {formatCurrency(summary.daily_sales.total)}
                          </Text>
                        </Group>
                      </Stack>
                    </Paper>
                  </SimpleGrid>
                  <Divider my="md" />
                  <Group justify="space-between" wrap="nowrap">
                    <Text
                      size="xs"
                      tt="uppercase"
                      fw={700}
                      c={overShortColor}
                      style={{ letterSpacing: "0.08em" }}
                    >
                      Cash Over/Short By — {overShortLabel}
                    </Text>
                    <Text fz={22} fw={700} c={overShortColor}>
                      {overShort === 0
                        ? "—"
                        : formatCurrency(Math.abs(overShort))}
                    </Text>
                  </Group>
                </Paper>
              </Stack>
            )}
          </Box>
        </ScrollArea>
      </Box>
    </Modal>
  );
};

export default CashDrawerBalancingSummaryModal;
